<template>
  <div class="page-container">
    <div class="page-header">
      <div>
        <h2 class="page-title">公司管理</h2>
      </div>
      <div class="page-header-stat">
        <span>共 <b>{{ flatList.length }}</b> 家公司</span>
        <span class="divider">|</span>
        <span>主公司 <b>{{ mainCount }}</b></span>
        <span class="divider">|</span>
        <span>合作公司 <b>{{ partnerCount }}</b></span>
      </div>
    </div>

    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form" @submit.native.prevent>
          <el-form-item label="公司名称">
            <el-input v-model="query.keyword" placeholder="公司全称或简称" clearable style="width: 200px" />
          </el-form-item>
          <el-form-item label="公司类型">
            <el-select v-model="query.type" placeholder="全部" clearable style="width: 130px">
              <el-option label="主公司" :value="1" />
              <el-option label="合作公司" :value="2" />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="query.status" placeholder="全部" clearable style="width: 110px">
              <el-option label="正常" :value="1" />
              <el-option label="停用" :value="0" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>

        <!-- 操作按钮区 -->
        <div class="action-bar">
          <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd">新增公司</el-button>
          <el-button
            icon="el-icon-delete"
            size="small"
            :disabled="multipleSelection.length === 0"
            @click="handleBatchDelete"
          >批量删除</el-button>
        </div>
      </div>
    </el-card>

    <el-card class="table-card" shadow="never">
      <el-table
        ref="table"
        v-loading="loading"
        :data="tableTree"
        row-key="id"
        border
        size="small"
        :tree-props="{ children: 'children' }"
        default-expand-all
        @selection-change="handleSelectionChange"
        height="100%"
      >
        <el-table-column type="selection" width="46"/>
        <el-table-column label="LOGO" width="80" align="center">
          <template slot-scope="scope">
            <el-image v-if="scope.row.logo" :src="scope.row.logo" :preview-src-list="[scope.row.logo]" fit="cover" class="company-logo" />
            <span v-else class="company-logo company-logo-text">{{ logoText(scope.row) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="公司名称" min-width="240" show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="company-name">{{ scope.row.name }}</span>
            <span v-if="scope.row.short_name" class="company-short">（{{ scope.row.short_name }}）</span>
          </template>
        </el-table-column>
        <el-table-column prop="type" label="类型" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.type === 1 ? 'danger' : 'primary'" size="mini" effect="plain">
              {{ scope.row.type === 1 ? '主公司' : '合作公司' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="contact_name" label="联系人" width="100"/>
        <el-table-column prop="contact_phone" label="联系电话" width="130"/>
        <el-table-column label="所在地区" min-width="160" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ regionText(scope.row) || '—' }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-switch
              v-model="scope.row.status"
              :active-value="1"
              :inactive-value="0"
              @change="val => handleStatusChange(scope.row, val)"
            />
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="150"/>
        <el-table-column label="操作" width="200" fixed="right" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="handleAddChild(scope.row)">加下级</el-button>
            <el-button type="text" size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" size="mini" class="btn-danger" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <company-form
      :visible.sync="formVisible"
      :row="currentRow"
      :company-options="allOptions"
      @saved="handleSaved"
    />
  </div>
</template>

<script>
import {
  getCompanyList,
  deleteCompany,
  batchDeleteCompany,
  changeCompanyStatus
} from '@/api/system'
import CompanyForm from './components/CompanyForm.vue'

export default {
  name: 'CompanyManage',
  components: { CompanyForm },
  data() {
    return {
      loading: false,
      list: [],
      query: { keyword: '', type: '', status: '' },
      multipleSelection: [],
      formVisible: false,
      currentRow: null
    }
  },
  computed: {
    flatList() {
      return this.list
    },
    mainCount() {
      return this.list.filter(i => i.type === 1).length
    },
    partnerCount() {
      return this.list.filter(i => i.type === 2).length
    },
    allOptions() {
      return this.list.map(i => ({ id: i.id, name: i.short_name || i.name }))
    },
    tableTree() {
      return this.buildTree(this.list)
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    // 全量列表（无分页，前端建树），筛选条件走服务端过滤
    loadData() {
      this.loading = true
      const params = {}
      const kw = (this.query.keyword || '').trim()
      if (kw) params.keyword = kw
      if (this.query.type !== '' && this.query.type !== null) params.type = this.query.type
      if (this.query.status !== '' && this.query.status !== null) params.status = this.query.status

      getCompanyList(params).then(res => {
        this.list = (res.data && res.data.list) || []
      }).finally(() => {
        this.loading = false
      })
    },
    buildTree(list) {
      const map = {}
      const roots = []
      list.forEach(item => {
        map[item.id] = Object.assign({}, item, { children: [] })
      })
      list.forEach(item => {
        const node = map[item.id]
        if (item.parent_id && map[item.parent_id] && item.parent_id !== item.id) {
          map[item.parent_id].children.push(node)
        } else {
          roots.push(node)
        }
      })
      return roots
    },
    // 收集某节点的全部子孙 id（编辑时避免把上级指向自己的后代）
    collectDescendants(id) {
      const result = []
      const walk = pid => {
        this.list.forEach(item => {
          if (item.parent_id === pid) {
            result.push(item.id)
            walk(item.id)
          }
        })
      }
      walk(id)
      return result
    },
    regionText(row) {
      // 新数据存编码，后端已换名附 _text；历史数据存中文名，查不到时兜底显示原值
      const text = [row.province_text, row.city_text, row.district_text].filter(Boolean).join(' / ')
      if (text) return text
      return [row.province, row.city, row.district].filter(Boolean).join(' / ')
    },
    // 无 LOGO 时取简称（或全称）前两个字做占位
    logoText(row) {
      const src = (row.short_name || row.name || '').trim()
      return src.slice(0, 2) || '—'
    },
    handleSearch() {
      this.loadData()
    },
    resetSearch() {
      this.query = { keyword: '', type: '', status: '' }
      this.loadData()
    },
    handleSelectionChange(val) {
      this.multipleSelection = val
    },
    handleAdd() {
      this.currentRow = null
      this.formVisible = true
    },
    handleAddChild(row) {
      this.currentRow = { type: 2, parent_id: row.id, status: 1 }
      this.formVisible = true
    },
    handleEdit(row) {
      this.currentRow = Object.assign({}, row)
      this.formVisible = true
    },
    // 新增/编辑由 CompanyForm 直连接口，成功后这里刷新列表
    handleSaved() {
      this.loadData()
    },
    handleStatusChange(row, val) {
      changeCompanyStatus(row.id, { status: val }).then(() => {
        this.$message.success(val === 1 ? '已启用' : '已停用')
      }).catch(() => {
        // 失败回滚开关，错误提示由拦截器统一弹
        row.status = val === 1 ? 0 : 1
      })
    },
    handleDelete(row) {
      // 有下级公司/挂部门/挂人员时后端会拒绝，提示由拦截器统一弹
      this.$confirm(`确认删除「${row.short_name || row.name}」？`, '删除确认', { type: 'warning' }).then(() => {
        deleteCompany(row.id).then(() => {
          this.$message.success('已删除')
          this.loadData()
        })
      }).catch(() => {})
    },
    handleBatchDelete() {
      const ids = this.multipleSelection.map(i => i.id)
      if (!ids.length) return
      this.$confirm(`确认删除选中的 ${ids.length} 家公司？`, '批量删除', { type: 'warning' }).then(() => {
        batchDeleteCompany({ ids }).then(() => {
          this.$message.success(`已删除 ${ids.length} 家`)
          this.loadData()
        })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
/* padding 由全局 page.css 的 .page-container 统一控制 */
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 16px;
}
.page-title {
  margin: 0 0 6px;
  font-size: 19px;
  font-weight: 500;
}
.page-header-stat {
  font-size: 13px;
  color: #606266;
}
.page-header-stat b {
  color: var(--primary);
  margin: 0 2px;
}
.page-header-stat .divider {
  color: #dcdfe6;
  margin: 0 8px;
}
.search-card {
  margin-bottom: 12px;
}
.company-name {
  color: #303133;
}
/* LOGO 缩略图 / 无图时的文字占位 */
.company-logo {
  width: 34px;
  height: 34px;
  border-radius: 6px;
  object-fit: cover;
  display: inline-block;
  vertical-align: middle;
}
.company-logo-text {
  line-height: 34px;
  background: var(--primary-300);
  color: var(--primary);
  font-size: 12px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
}
.company-short {
  color: #909399;
  font-size: 12px;
}
.btn-danger {
  color: #f56c6c;
}
.btn-danger:hover {
  color: #f78989;
}
</style>
